import { isAxiosError, isCancel } from "axios";

export const DEFAULT_CALCULATION_RETRY_AFTER_MS = 5_000;
export const DEFAULT_CALCULATION_CACHE_TTL_MS = 5_000;
export const DEFAULT_CALCULATION_CACHE_MAX_ENTRIES = 128;

const ABORT_MESSAGE = "The calculation request was cancelled.";

function normalizeNonNegativeDuration(
  value: number,
  fallback: number,
): number {
  return Number.isFinite(value) && value >= 0 ? value : fallback;
}

function firstHeaderValue(value: unknown): unknown {
  return Array.isArray(value) ? value[0] : value;
}

/**
 * Parse Retry-After using its RFC forms: delta seconds or an HTTP date.
 * Invalid/missing values use a small fallback so a bare gateway 429 still has
 * a deterministic retry window.
 */
export function parseRetryAfterMs(
  value: unknown,
  nowMs = Date.now(),
  fallbackMs = DEFAULT_CALCULATION_RETRY_AFTER_MS,
): number {
  const safeFallback = normalizeNonNegativeDuration(
    fallbackMs,
    DEFAULT_CALCULATION_RETRY_AFTER_MS,
  );
  const candidate = firstHeaderValue(value);

  if (typeof candidate === "number") {
    return Number.isFinite(candidate) && candidate >= 0
      ? Math.ceil(candidate * 1_000)
      : safeFallback;
  }

  if (typeof candidate !== "string") return safeFallback;
  const trimmed = candidate.trim();
  if (!trimmed) return safeFallback;

  if (/^\d+(?:\.\d+)?$/.test(trimmed)) {
    const seconds = Number(trimmed);
    return Number.isFinite(seconds)
      ? Math.ceil(seconds * 1_000)
      : safeFallback;
  }

  const retryAtMs = Date.parse(trimmed);
  return Number.isFinite(retryAtMs)
    ? Math.max(0, retryAtMs - nowMs)
    : safeFallback;
}

function readHeader(headers: unknown, name: string): unknown {
  if (!headers || typeof headers !== "object") return undefined;

  const headerContainer = headers as {
    get?: (headerName: string) => unknown;
  };
  if (typeof headerContainer.get === "function") {
    const value = headerContainer.get(name);
    if (value !== undefined && value !== null) return value;
  }

  const wanted = name.toLowerCase();
  const matchingEntry = Object.entries(headers).find(
    ([headerName]) => headerName.toLowerCase() === wanted,
  );
  return matchingEntry?.[1];
}

function retryMessage(retryAfterMs: number): string {
  if (retryAfterMs <= 0) {
    return "Too many calculation requests. Please retry now.";
  }

  const seconds = Math.max(1, Math.ceil(retryAfterMs / 1_000));
  const unit = seconds === 1 ? "second" : "seconds";
  return `Too many calculation requests. Please retry in ${seconds} ${unit}.`;
}

/** A user-facing, schedulable representation of an HTTP 429 response. */
export class CalculationRateLimitError extends Error {
  readonly retryAfterMs: number;
  readonly retryAtMs: number;
  readonly sourceError: unknown;

  constructor(args: {
    retryAfterMs: number;
    retryAtMs: number;
    sourceError?: unknown;
  }) {
    const safeRetryAfterMs = normalizeNonNegativeDuration(
      args.retryAfterMs,
      DEFAULT_CALCULATION_RETRY_AFTER_MS,
    );
    super(retryMessage(safeRetryAfterMs));
    this.name = "CalculationRateLimitError";
    this.retryAfterMs = safeRetryAfterMs;
    this.retryAtMs = args.retryAtMs;
    this.sourceError = args.sourceError;
  }
}

export function isCalculationRateLimitError(
  error: unknown,
): error is CalculationRateLimitError {
  return error instanceof CalculationRateLimitError;
}

/** Convert an Axios HTTP 429 into the typed calculation error; ignore others. */
export function toCalculationRateLimitError(
  error: unknown,
  options: { nowMs?: number; fallbackMs?: number } = {},
): CalculationRateLimitError | undefined {
  if (isCalculationRateLimitError(error)) return error;
  if (!isAxiosError(error) || error.response?.status !== 429) return undefined;

  const nowMs = options.nowMs ?? Date.now();
  const retryAfterMs = parseRetryAfterMs(
    readHeader(error.response.headers, "retry-after"),
    nowMs,
    options.fallbackMs,
  );
  return new CalculationRateLimitError({
    retryAfterMs,
    retryAtMs: nowMs + retryAfterMs,
    sourceError: error,
  });
}

export interface RetryAfterGateOptions {
  now?: () => number;
  fallbackMs?: number;
}

/**
 * Process-wide gate for one API family. Once any caller observes a 429, all
 * callers sharing this instance fail locally until Retry-After has elapsed.
 */
export class RetryAfterGate {
  private readonly now: () => number;
  private readonly fallbackMs: number;
  private blockedUntilMs = 0;
  private sourceError: unknown;

  constructor(options: RetryAfterGateOptions = {}) {
    this.now = options.now ?? (() => Date.now());
    this.fallbackMs = normalizeNonNegativeDuration(
      options.fallbackMs ?? DEFAULT_CALCULATION_RETRY_AFTER_MS,
      DEFAULT_CALCULATION_RETRY_AFTER_MS,
    );
  }

  get retryAtMs(): number {
    this.currentError();
    return this.blockedUntilMs;
  }

  clear(): void {
    this.blockedUntilMs = 0;
    this.sourceError = undefined;
  }

  throwIfBlocked(): void {
    const error = this.currentError();
    if (error) throw error;
  }

  record(error: unknown): CalculationRateLimitError | undefined {
    const nowMs = this.now();
    const rateLimitError = toCalculationRateLimitError(error, {
      nowMs,
      fallbackMs: this.fallbackMs,
    });
    if (!rateLimitError) return undefined;

    if (rateLimitError.retryAtMs > this.blockedUntilMs) {
      this.blockedUntilMs = rateLimitError.retryAtMs;
      this.sourceError = rateLimitError.sourceError ?? error;
    }
    return this.currentError(nowMs) ?? rateLimitError;
  }

  async run<T>(request: () => Promise<T>): Promise<T> {
    this.throwIfBlocked();
    try {
      return await request();
    } catch (error) {
      const rateLimitError = this.record(error);
      if (rateLimitError) throw rateLimitError;
      throw error;
    }
  }

  private currentError(nowMs = this.now()): CalculationRateLimitError | undefined {
    const retryAfterMs = this.blockedUntilMs - nowMs;
    if (retryAfterMs <= 0) {
      this.clear();
      return undefined;
    }
    return new CalculationRateLimitError({
      retryAfterMs,
      retryAtMs: this.blockedUntilMs,
      sourceError: this.sourceError,
    });
  }
}

/** True for browser AbortError and Axios cancellation variants. */
export function isRequestAbortError(error: unknown): boolean {
  if (isCancel(error)) return true;
  if (!error || typeof error !== "object") return false;

  const candidate = error as {
    name?: unknown;
    code?: unknown;
    message?: unknown;
  };
  if (
    candidate.name === "AbortError" ||
    candidate.name === "CanceledError" ||
    candidate.code === "ABORT_ERR" ||
    candidate.code === "ERR_CANCELED"
  ) {
    return true;
  }

  const message =
    typeof candidate.message === "string" ? candidate.message : "";
  return /request aborted|operation was aborted|user aborted a request/i.test(
    message,
  );
}

export function createRequestAbortError(): DOMException {
  return new DOMException(ABORT_MESSAGE, "AbortError");
}

function abortErrorForSignal(signal: AbortSignal): unknown {
  return isRequestAbortError(signal.reason)
    ? signal.reason
    : createRequestAbortError();
}

function stableNormalize(
  value: unknown,
  ancestors: WeakSet<object>,
  arrayEntry: boolean,
): unknown {
  if (
    value === undefined ||
    typeof value === "function" ||
    typeof value === "symbol"
  ) {
    return arrayEntry ? null : undefined;
  }
  if (typeof value === "bigint") {
    throw new TypeError("BigInt cannot be serialized in a request key.");
  }
  if (value === null || typeof value !== "object") return value;

  if (ancestors.has(value)) {
    throw new TypeError("Circular value cannot be serialized in a request key.");
  }
  ancestors.add(value);

  let normalized: unknown;
  if (value instanceof Date) {
    normalized = value.toJSON();
  } else if (Array.isArray(value)) {
    normalized = value.map((entry) => stableNormalize(entry, ancestors, true));
  } else {
    const record = value as Record<string, unknown>;
    const output: Record<string, unknown> = {};
    for (const key of Object.keys(record).sort()) {
      const entry = stableNormalize(record[key], ancestors, false);
      if (entry !== undefined) output[key] = entry;
    }
    normalized = output;
  }

  ancestors.delete(value);
  return normalized;
}

/** JSON-compatible serialization with recursively sorted object keys. */
export function stableSerializeRequest(value: unknown): string {
  const serialized = JSON.stringify(
    stableNormalize(value, new WeakSet<object>(), false),
  );
  return serialized ?? "undefined";
}

export function createStableRequestKey(
  namespace: string,
  value: unknown,
): string {
  return `${namespace}:${stableSerializeRequest(value)}`;
}

export interface SharedRequestCacheOptions {
  ttlMs?: number;
  maxEntries?: number;
  now?: () => number;
}

interface PendingEntry<T> {
  readonly kind: "pending";
  readonly controller: AbortController;
  readonly consumers: Set<symbol>;
  readonly promise: Promise<T>;
}

interface FulfilledEntry<T> {
  readonly kind: "fulfilled";
  readonly value: T;
  readonly expiresAtMs: number;
}

type CacheEntry<T> = PendingEntry<T> | FulfilledEntry<T>;

/**
 * Bounded in-process TTL cache with single-flight request sharing.
 *
 * Each caller remains an independent consumer: aborting one consumer rejects
 * only that caller. The underlying request is aborted once no active consumer
 * remains. Loader failures are removed immediately and are never cached.
 */
export class SharedRequestCache<T> {
  private readonly entries = new Map<string, CacheEntry<T>>();
  private readonly ttlMs: number;
  private readonly maxEntries: number;
  private readonly now: () => number;

  constructor(options: SharedRequestCacheOptions = {}) {
    this.ttlMs = normalizeNonNegativeDuration(
      options.ttlMs ?? DEFAULT_CALCULATION_CACHE_TTL_MS,
      DEFAULT_CALCULATION_CACHE_TTL_MS,
    );
    const requestedMax =
      options.maxEntries ?? DEFAULT_CALCULATION_CACHE_MAX_ENTRIES;
    this.maxEntries =
      Number.isInteger(requestedMax) && requestedMax > 0
        ? requestedMax
        : DEFAULT_CALCULATION_CACHE_MAX_ENTRIES;
    this.now = options.now ?? (() => Date.now());
  }

  get size(): number {
    this.removeExpired();
    return this.entries.size;
  }

  async getOrCreate(
    key: string,
    load: (signal: AbortSignal) => Promise<T>,
    signal?: AbortSignal,
  ): Promise<T> {
    if (signal?.aborted) throw abortErrorForSignal(signal);
    this.removeExpired();

    const existing = this.entries.get(key);
    if (existing?.kind === "fulfilled") {
      this.touch(key, existing);
      return existing.value;
    }
    if (existing?.kind === "pending") {
      this.touch(key, existing);
      return this.subscribe(existing, signal, key);
    }

    const shouldStore = this.makeRoom();
    const entry = this.createPending(load);
    if (shouldStore) this.entries.set(key, entry);
    void this.observe(key, entry, shouldStore);
    return this.subscribe(entry, signal, shouldStore ? key : undefined);
  }

  delete(key: string): void {
    const entry = this.entries.get(key);
    this.entries.delete(key);
    if (entry?.kind === "pending") entry.controller.abort();
  }

  /** Remove reusable results without disturbing active shared requests. */
  clearFulfilled(): void {
    for (const [key, entry] of this.entries) {
      if (entry.kind === "fulfilled") this.entries.delete(key);
    }
  }

  /** Full teardown for explicit cancellation and test cleanup. */
  clear(): void {
    const pendingEntries = [...this.entries.values()].filter(
      (entry): entry is PendingEntry<T> => entry.kind === "pending",
    );
    this.entries.clear();
    for (const entry of pendingEntries) entry.controller.abort();
  }

  private createPending(
    load: (signal: AbortSignal) => Promise<T>,
  ): PendingEntry<T> {
    const controller = new AbortController();
    return {
      kind: "pending",
      controller,
      consumers: new Set<symbol>(),
      promise: this.execute(load, controller.signal),
    };
  }

  private async execute(
    load: (signal: AbortSignal) => Promise<T>,
    signal: AbortSignal,
  ): Promise<T> {
    return await load(signal);
  }

  private async observe(
    key: string,
    entry: PendingEntry<T>,
    stored: boolean,
  ): Promise<void> {
    try {
      const value = await entry.promise;
      if (stored && this.entries.get(key) === entry) {
        this.entries.delete(key);
        this.entries.set(key, {
          kind: "fulfilled",
          value,
          expiresAtMs: this.now() + this.ttlMs,
        });
      }
    } catch {
      if (stored && this.entries.get(key) === entry) this.entries.delete(key);
    }
  }

  private subscribe(
    entry: PendingEntry<T>,
    signal?: AbortSignal,
    storedKey?: string,
  ): Promise<T> {
    const consumer = Symbol("calculation-request-consumer");
    entry.consumers.add(consumer);

    return new Promise<T>((resolve, reject) => {
      let active = true;

      const cleanup = () => {
        signal?.removeEventListener("abort", onConsumerAbort);
        entry.controller.signal.removeEventListener(
          "abort",
          onUnderlyingAbort,
        );
      };
      const finish = (complete: () => void) => {
        if (!active) return;
        active = false;
        entry.consumers.delete(consumer);
        cleanup();
        complete();
      };
      const onConsumerAbort = () => {
        finish(() => {
          reject(signal ? abortErrorForSignal(signal) : createRequestAbortError());
          if (entry.consumers.size === 0) {
            if (storedKey && this.entries.get(storedKey) === entry) {
              this.entries.delete(storedKey);
            }
            entry.controller.abort();
          }
        });
      };
      const onUnderlyingAbort = () => {
        finish(() => reject(abortErrorForSignal(entry.controller.signal)));
      };

      signal?.addEventListener("abort", onConsumerAbort, { once: true });
      entry.controller.signal.addEventListener("abort", onUnderlyingAbort, {
        once: true,
      });

      const waitForEntry = async () => {
        try {
          const value = await entry.promise;
          finish(() => resolve(value));
        } catch (error) {
          finish(() => reject(error));
        }
      };
      void waitForEntry();
    });
  }

  private removeExpired(): void {
    const nowMs = this.now();
    for (const [key, entry] of this.entries) {
      if (entry.kind === "fulfilled" && entry.expiresAtMs <= nowMs) {
        this.entries.delete(key);
      }
    }
  }

  private makeRoom(): boolean {
    if (this.entries.size < this.maxEntries) return true;

    for (const [key, entry] of this.entries) {
      if (entry.kind === "fulfilled") {
        this.entries.delete(key);
        return true;
      }
    }

    // All slots are active requests. Run this request uncached instead of
    // evicting/cancelling an in-use request or letting the map grow unbounded.
    return false;
  }

  private touch(key: string, entry: CacheEntry<T>): void {
    this.entries.delete(key);
    this.entries.set(key, entry);
  }
}
